import React from 'react';
import { FaCheckCircle, FaFileAlt, FaLock, FaTimesCircle } from 'react-icons/fa';
import { getDocumentUrl } from '../../../../services/apiService';
import {
  buildRegistrationSections,
  collectRegistrationDocuments,
  formatPartnerFieldKey,
} from '../utils/partnerRegistrationAdmin';

const SENSITIVE_KEYS = ['password', 'confirmPassword'];

function isSensitive(k) {
  return SENSITIVE_KEYS.some((sk) => String(k).toLowerCase().includes(sk.toLowerCase()));
}

function isDocPath(v) {
  return typeof v === 'string' && (v.startsWith('/uploads/') || v.startsWith('uploads/') || v.startsWith('http'));
}

function FieldGrid({ data }) {
  if (!data || !Object.keys(data).length) return <p className="text-sm text-gray-500">—</p>;
  return (
    <div className="divide-y divide-gray-100 rounded-lg border border-gray-100 overflow-hidden">
      {Object.entries(data).map(([key, value]) => {
        const isBool = value === true || value === false;
        return (
          <div key={key} className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-4 px-4 py-3 bg-white even:bg-gray-50/40">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide sm:w-44 shrink-0">
              {formatPartnerFieldKey(key)}
            </span>
            <span className="text-sm text-gray-900 break-words flex-1">
              {isBool ? (
                <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-xs font-medium ${value ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>
                  {value ? <FaCheckCircle /> : <FaTimesCircle />}
                  {value ? 'Yes' : 'No'}
                </span>
              ) : isSensitive(key) ? (
                <span className="inline-flex items-center gap-1.5 text-gray-500">
                  <FaLock className="text-xs" /> ••••••••
                </span>
              ) : (
                String(value ?? '—')
              )}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function isImageUrl(url) {
  return /\.(jpe?g|png|gif|webp)(\?|$)/i.test(url || '');
}

export default function PartnerRegistrationDetail({ user }) {
  const sections = buildRegistrationSections(user);
  const docs = collectRegistrationDocuments(user);

  return (
    <div className="space-y-6">
      {sections.length === 0 ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-4 text-sm text-amber-900">
          No registration data found on this account. The user may have signed up before data saving was fixed — ask them to register again.
        </div>
      ) : (
        sections.map((section) => (
          <div key={section.title}>
            <h4 className="text-sm font-semibold text-gray-800 mb-2">{section.title}</h4>
            <FieldGrid data={section.data} />
          </div>
        ))
      )}

      <div>
        <h4 className="text-sm font-semibold text-gray-800 mb-3">Uploaded documents</h4>
        {docs.length === 0 ? (
          <p className="text-sm text-gray-500">No documents uploaded.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {docs.map((doc) => {
              const url = getDocumentUrl(doc.path);
              const image = isImageUrl(url) || isImageUrl(doc.path);
              return (
                <div key={doc.path} className="rounded-xl border border-gray-200 overflow-hidden bg-white">
                  {image ? (
                    <a href={url} target="_blank" rel="noopener noreferrer" className="block">
                      <img src={url} alt={doc.label} className="w-full h-40 object-cover bg-gray-100" />
                    </a>
                  ) : (
                    <div className="h-40 flex items-center justify-center bg-gray-50 text-gray-400">
                      <FaFileAlt className="text-3xl" />
                    </div>
                  )}
                  <div className="p-3 border-t border-gray-100">
                    <p className="text-sm font-medium text-gray-800 truncate">{doc.label}</p>
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-sky-600 hover:underline mt-1 inline-block"
                    >
                      Open file
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
